import React from 'react';

import { defaultWorkflowFileSort, getEditorWorkflowRoute } from '@aline/common';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AlineButton, AlineTooltip } from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import AlineInfoImage from 'src/images/launch-success.png';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { getWorkflowWasSentForSig } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  infoCard: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    justifyContent: 'center',
  },
  infoSection: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    justifyContent: 'center',
    width: 504,
  },
  successImage: {
    height: 220,
    width: 284,
  },
}));

interface LaunchSuccessAction extends Pick<
  Apollo.WorkflowAction,
  'completedAt' | 'id' | '__typename'
> {
  permission: WorkflowPermissionSubset;
}
interface LaunchSuccessStep extends Pick<Apollo.WorkflowStep, 'id' | '__typename'> {
  actions: LaunchSuccessAction[];
}
interface LaunchSuccessFile extends Pick<Apollo.WorkflowFile, 'id' | 'orderId' | '__typename'> {
  document: Pick<Apollo.Document, 'id' | '__typename'>;
}
interface LaunchSuccessWorkflow extends Pick<Apollo.Workflow, 'id' | 'state' | '__typename'> {
  activeStep: LaunchSuccessStep | null;
  files: LaunchSuccessFile[];
}
export interface LaunchSuccessProps {
  launchedWorkflow: LaunchSuccessWorkflow;
}

export function LaunchSuccess({ launchedWorkflow }: LaunchSuccessProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const currentUser = useSelectCurrentUser();
  const loggedInEmail = currentUser?.email?.toLowerCase();
  const canTakeActionOnActiveStep = getWorkflowWasSentForSig(launchedWorkflow);
  const requesterHasIncompleteActions = Boolean(
    launchedWorkflow.activeStep?.actions.some(a => {
      const isActionAssignedToRequester =
        Boolean(loggedInEmail) &&
        a.permission.account?.__typename === 'User' &&
        a.permission.account.email.toLowerCase() === loggedInEmail;

      return isActionAssignedToRequester && !a.completedAt;
    })
  );
  const requesterHasActionsToDo = canTakeActionOnActiveStep && requesterHasIncompleteActions;
  const documentId = [...launchedWorkflow.files].sort(defaultWorkflowFileSort).at(0)?.document.id;

  return (
    <div className={classes.infoSection}>
      <img className={classes.successImage} src={AlineInfoImage} srcSet={AlineInfoImage} />
      <div className={classes.infoCard}>
        <Typography align="center" component="h1" variant="h1">
          Success!
        </Typography>
        <Typography align="center">
          {requesterHasActionsToDo
            ? 'Congratulations! You’ve successfully launched your workflow. It looks like you have something to do right now.'
            : 'Congratulations! You’ve successfully launched your workflow. There is nothing for you to do right now.'}
        </Typography>
        {requesterHasActionsToDo && (
          <AlineTooltip arrow placement="top" title={!documentId ? 'No documents in workflow' : ''}>
            <div>
              <AlineButton
                color="primary"
                disabled={!documentId}
                variant="contained"
                onClick={() => {
                  if (!documentId) return;
                  navigate(
                    getEditorWorkflowRoute(documentId, launchedWorkflow.id, {
                      walkThroughParams: {
                        autoStartWalkthrough: requesterHasActionsToDo,
                      },
                    })
                  );
                }}
              >
                Get to Work
              </AlineButton>
            </div>
          </AlineTooltip>
        )}
      </div>
    </div>
  );
}
